/**
 * Centrifugo transport layer for PvP Roulette.
 *
 * Bootstrap (on room open): initial snapshot is provided by `/v1/bootstrap`
 * in `PvpRoomRoot`, then this transport subscribes to realtime channel updates.
 *
 * Both may deliver snapshots. Version guard applies only the freshest.
 * Subscription is per room — no resubscribe on round change.
 *
 * After each (re)subscribe, we fetch the latest publication from Centrifugo
 * history (when enabled on the channel) to reduce gaps after reconnect / token
 * refresh.
 *
 * File layout:
 *   1. Imports
 *   2. Public channel builders / event types (room, online, balance, gifts)
 *   3. Public handler / option types
 *   4. Internal types
 *   5. Module-private state
 *   6. Token-recovery helpers
 *   7. Centrifuge client bootstrap (`getClient`)
 *   8. Per-channel bridges (publication / error / history replay)
 *   9. Subscription lifecycle (`ensureSubscription`, `teardownSubscription`)
 *  10. Online-channel helpers
 *  11. Per-user fan-out factory (`createUserChannelSubscriber`)
 *  12. Public API: `subscribeRoom`, `subscribeOnlineCount`, `subscribeBalance`,
 *                  `subscribeGiftInventory`, `unsubscribeRoom`, `disconnectCentrifugo`
 */

// ─── 1. Imports ──────────────────────────────────────────────────────────────

import { Centrifuge, Subscription, UnauthorizedError } from "centrifuge"
import { getRuntimeConfig } from "@/shared/config"
import { apiClient } from "@/shared/api"
import { getAccessToken, isAccessTokenExpired } from "@/shared/api/auth"
import type { RoundSnapshot } from "@/shared/api/types/pvpRoulette"
import type { UserGift } from "@/shared/api/types"
import type { TournamentUpdatedEvent } from "@/shared/api/types/tournaments"
import { getCurrentRound, parseRoundSnapshotPayload } from "@/shared/api/round"
import {
  applyLatestAccessTokenToCentrifugo,
  setCentrifugeTokenSyncTarget,
} from "./centrifugoTokenSync"
import { ServerClockOffsetEstimator } from "./serverClockOffset"

// ─── 2. Public channel builders & event payloads ─────────────────────────────

/** Channel format per contract: `realtime-events:games:pvp-roulette:room:{roomName}`. */
export const PVP_ROULETTE_CHANNEL_PREFIX =
  "realtime-events:games:pvp-roulette:room:"

export function buildRoomChannel(roomId: string): string {
  return `${PVP_ROULETTE_CHANNEL_PREFIX}${roomId}`
}

/**
 * User-limited channel: Centrifugo accepts the subscription only when the
 * `#<user-id>` suffix matches the `sub` claim of the connection JWT.
 */
export const BALANCE_CHANNEL_PREFIX = "realtime-personal:balance:user:"

export function buildBalanceChannel(accountId: string): string {
  return `${BALANCE_CHANNEL_PREFIX}${accountId}#${accountId}`
}

/** User-limited counterpart of the balance feed. */
export const GIFT_INVENTORY_CHANNEL_PREFIX = "realtime-personal:gifts:user:"

export function buildGiftInventoryChannel(accountId: string): string {
  return `${GIFT_INVENTORY_CHANNEL_PREFIX}${accountId}#${accountId}`
}

/**
 * Tournaments feed — a single static, global channel (not user-scoped). One
 * subscription for all clients; the broadcast carries no per-user data
 * (`my_standing` is always null here — see `useTournamentStore`).
 */
export const TOURNAMENTS_CHANNEL = "realtime-events:games:tournaments"

export function buildTournamentsChannel(): string {
  return TOURNAMENTS_CHANNEL
}

export interface BalanceUpdatedEvent {
  type: "BalanceUpdated"
  asset_class: "TON" | "TELEGRAM_GIFT"
  /** Final balance in atomic units (nanotons for TON). Not a delta. */
  new_balance: number
}

/**
 * Server snapshot of the user's gift inventory, broadcast after any change
 * (deposit, withdrawal, bet, hold release). `items` is the full list — clients
 * should replace their local cache with it.
 */
export interface GiftInventoryUpdatedEvent {
  type: "GiftInventoryUpdated"
  items: UserGift[]
}

// ─── 3. Public handler / option types ────────────────────────────────────────

export type SnapshotSource = "api" | "realtime" | "history"

export type BalanceEventHandler = (event: BalanceUpdatedEvent) => void
export type GiftInventoryEventHandler = (
  event: GiftInventoryUpdatedEvent
) => void

export interface RoomHandlers {
  /**
   * Called for any round snapshot.
   * `source` indicates origin for version-race handling. `clockOffsetMs` is
   * set only for `realtime` source (computed from `server_time` envelope).
   */
  onSnapshot: (
    snapshot: RoundSnapshot,
    source: SnapshotSource,
    clockOffsetMs?: number
  ) => void
  onError: (err: Error) => void
}

export interface SubscribeRoomOptions {
  /** Skip initial API fetch (e.g. emulator). Default `false` = always fetch. */
  skipApiFetch?: boolean
}

/**
 * Caller-facing options shared by all per-user subscribers. `onReconnect`
 * fires only on actual reconnects, never on the initial connect.
 */
export interface UserChannelSubscribeOptions<TEvent> {
  onEvent: (event: TEvent) => void
  onReconnect?: () => void
}

export type BalanceSubscribeOptions =
  UserChannelSubscribeOptions<BalanceUpdatedEvent>
export type GiftInventorySubscribeOptions =
  UserChannelSubscribeOptions<GiftInventoryUpdatedEvent>

// ─── 4. Internal types ───────────────────────────────────────────────────────

interface SubscriberSlot {
  cancelled: boolean
  onSnapshot: (
    snapshot: RoundSnapshot,
    source: SnapshotSource,
    clockOffsetMs?: number
  ) => void
  onError: (err: Error) => void
}

interface OnlineCountHandlers {
  onCount: (count: number) => void
  onError?: (err: Error) => void
}

interface OnlineSubscriberSlot {
  cancelled: boolean
  onCount: (count: number) => void
  onError?: (err: Error) => void
}

interface OnlineChannelState {
  slots: Set<OnlineSubscriberSlot>
  count: number | null
  isReconciling: boolean
  reconcileTimer: ReturnType<typeof setInterval> | null
  onSubscribed: () => void
  onJoin: () => void
  onLeave: () => void
  onError: (ctx: { error?: { code?: number; message?: string } }) => void
}

interface UserChannelSlot<TEvent> {
  cancelled: boolean
  onEvent: (event: TEvent) => void
  onReconnect?: () => void
}

interface UserChannelSubscriberConfig<TEvent> {
  /** Builds the Centrifugo channel string from the account identifier. */
  buildChannel: (accountId: string) => string
  /**
   * Validates and shapes the raw publication payload into a typed event.
   * Return `null` to drop the publication (wrong `type`, malformed shape).
   */
  parsePublication: (data: Record<string, unknown>) => TEvent | null
}

// ─── 5. Module-private state ─────────────────────────────────────────────────

export const ONLINE_RECONCILE_INTERVAL_MS = 30_000

/** Centrifugo error code: connection / subscription JWT expired (server reply). */
const CENTRIFUGO_TOKEN_EXPIRED_CODE = 109

/**
 * Shared estimator for server clock offset based only on realtime `server_time`.
 * Sign convention:
 * - offset > 0 means server clock is ahead of client clock
 * - adjustedNow = Date.now() + offset
 */
const serverClockOffsetEstimator = new ServerClockOffsetEstimator()

let centrifugeInstance: Centrifuge | null = null
let tokenRecoveryPromise: Promise<void> | null = null

const subscriptions = new Map<string, Subscription>()
const subRefCounts = new Map<string, number>()
const subscriberSlotsByChannel = new Map<string, Set<SubscriberSlot>>()
const publicationBridgeByChannel = new Map<
  string,
  (ctx: { data: unknown }) => void
>()
const errorBridgeByChannel = new Map<
  string,
  (ctx: { error?: { code?: number; message?: string } }) => void
>()
const subscribedHistoryHookByChannel = new Map<string, () => void>()
const onlineChannelStateByChannel = new Map<string, OnlineChannelState>()

// ─── 6. Token-recovery helpers ───────────────────────────────────────────────

/**
 * Called when Centrifugo returns 109 (token expired) on subscribe or client
 * errors. Refreshes session tokens and pushes a new JWT into Centrifuge
 * (reconnect if needed).
 */
function scheduleCentrifugoRecoveryAfterTokenExpiryError(): void {
  if (tokenRecoveryPromise) return
  tokenRecoveryPromise = (async () => {
    try {
      const ok = await apiClient.refreshAccessToken()
      if (!ok) {
        await apiClient.authenticateWithTelegram()
      }
      applyLatestAccessTokenToCentrifugo()
    } finally {
      tokenRecoveryPromise = null
    }
  })()
}

async function resolveCentrifugoConnectionToken(): Promise<string> {
  const existing = getAccessToken()
  if (existing && !isAccessTokenExpired()) return existing

  const refreshed = await apiClient.refreshAccessToken()
  if (refreshed) {
    const t = getAccessToken()
    if (t) return t
  }
  const authed = await apiClient.authenticateWithTelegram()
  if (!authed) {
    throw new UnauthorizedError("Centrifugo: not authenticated")
  }
  const t = getAccessToken()
  if (!t) {
    throw new UnauthorizedError("Centrifugo: missing access token")
  }
  return t
}

function attachCentrifugoTokenExpiryHandlers(client: Centrifuge): void {
  client.on("error", (ctx) => {
    const err = ctx.error as { code?: number } | undefined
    if (err?.code === CENTRIFUGO_TOKEN_EXPIRED_CODE) {
      scheduleCentrifugoRecoveryAfterTokenExpiryError()
    }
  })
}

// ─── 7. Centrifuge client bootstrap ──────────────────────────────────────────

function getClient(): Centrifuge {
  if (centrifugeInstance) return centrifugeInstance

  const config = getRuntimeConfig()
  const endpoint = config.centrifugoUrl
  if (!endpoint) {
    throw new Error(
      "[Centrifugo] centrifugoUrl is not configured. Please set centrifugoUrl in /config.json."
    )
  }

  const token = getAccessToken()
  if (!token) {
    throw new Error(
      "[Centrifugo] Missing access token. Connect only after successful auth."
    )
  }
  if (isAccessTokenExpired()) {
    throw new Error(
      "[Centrifugo] Access token is expired. Refresh session before connecting."
    )
  }

  // `getToken` is used after server error 109 and for connection JWT refresh
  // when Centrifugo sends `expires`.
  const options = {
    token,
    getToken: () => resolveCentrifugoConnectionToken(),
  }

  // Endpoint as-is. Centrifugo typically: `wss://host/connection/websocket`.
  centrifugeInstance = new Centrifuge(endpoint, options)
  setCentrifugeTokenSyncTarget(centrifugeInstance)
  attachCentrifugoTokenExpiryHandlers(centrifugeInstance)
  centrifugeInstance.connect()

  return centrifugeInstance
}

// ─── 8. Per-channel bridges ──────────────────────────────────────────────────

function createPublicationBridge(
  channel: string
): (ctx: { data: unknown }) => void {
  return (ctx: { data: unknown }) => {
    const snapshot = parseRoundSnapshotPayload(ctx.data)
    if (!snapshot) return

    // Extract server_time from the event envelope for clock-drift compensation.
    const data = ctx.data as Record<string, unknown> | null | undefined
    const serverTime =
      typeof data?.server_time === "number" ? data.server_time : undefined

    // Stable clock offset from realtime push stream.
    const clockOffsetMs =
      serverTime != null
        ? serverClockOffsetEstimator.ingestServerTime(serverTime, Date.now())
        : undefined

    if (
      import.meta.env.DEV &&
      clockOffsetMs != null &&
      Math.abs(clockOffsetMs) > 5000
    ) {
      console.warn(`[Centrifugo] Large server clock offset: ${clockOffsetMs}ms`)
    }

    const slots = subscriberSlotsByChannel.get(channel)
    if (!slots) return
    for (const slot of slots) {
      if (!slot.cancelled) slot.onSnapshot(snapshot, "realtime", clockOffsetMs)
    }
  }
}

function createErrorBridge(
  channel: string
): (ctx: { error?: { code?: number; message?: string } }) => void {
  return (ctx) => {
    if (ctx.error?.code === CENTRIFUGO_TOKEN_EXPIRED_CODE) {
      scheduleCentrifugoRecoveryAfterTokenExpiryError()
    }
    const err = new Error(String(ctx.error?.message ?? ctx.error))
    const slots = subscriberSlotsByChannel.get(channel)
    if (!slots) return
    for (const slot of slots) {
      if (!slot.cancelled) slot.onError(err)
    }
  }
}

async function replayLatestHistoryFromSubscription(
  channel: string,
  sub: Subscription
): Promise<void> {
  try {
    const { publications } = await sub.history({ limit: 1, reverse: true })
    const latest = publications[0]
    if (!latest) return
    const snapshot = parseRoundSnapshotPayload(latest.data)
    if (!snapshot) return
    const slots = subscriberSlotsByChannel.get(channel)
    if (!slots) return
    for (const slot of slots) {
      if (!slot.cancelled) slot.onSnapshot(snapshot, "history")
    }
  } catch (e) {
    if (import.meta.env.DEV) {
      console.warn(
        "[Centrifugo] history(limit:1) after subscribe skipped:",
        channel,
        e
      )
    }
  }
}

// ─── 9. Subscription lifecycle ───────────────────────────────────────────────

function ensureSubscription(channel: string): {
  sub: Subscription
  isNew: boolean
} {
  const client = getClient()
  let sub = subscriptions.get(channel)
  const isNew = !sub
  if (!sub) {
    // Server only emits join/leave into this connection when `subscribe`
    // carries `join_leave: true`. Namespace `join_leave: true` alone is not
    // enough; see centrifuge-js `SubscriptionOptions.joinLeave`.
    sub = client.newSubscription(channel, { joinLeave: true })
    subscriptions.set(channel, sub)
    subRefCounts.set(channel, 0)

    const pubBridge = createPublicationBridge(channel)
    publicationBridgeByChannel.set(channel, pubBridge)
    sub.on("publication", pubBridge)

    const errBridge = createErrorBridge(channel)
    errorBridgeByChannel.set(channel, errBridge)
    sub.on("error", errBridge)

    const historyHook = () => {
      void replayLatestHistoryFromSubscription(channel, sub!)
    }
    subscribedHistoryHookByChannel.set(channel, historyHook)
    sub.on("subscribed", historyHook)
  }

  subRefCounts.set(channel, (subRefCounts.get(channel) ?? 0) + 1)
  return { sub, isNew }
}

function teardownSubscription(channel: string): void {
  const sub = subscriptions.get(channel)
  if (!sub) return

  const onlineState = onlineChannelStateByChannel.get(channel)
  if (onlineState) {
    if (onlineState.reconcileTimer) {
      clearInterval(onlineState.reconcileTimer)
      onlineState.reconcileTimer = null
    }
    sub.off("subscribed", onlineState.onSubscribed)
    sub.off("join", onlineState.onJoin)
    sub.off("leave", onlineState.onLeave)
    sub.off("error", onlineState.onError)
    onlineChannelStateByChannel.delete(channel)
  }

  const pubBridge = publicationBridgeByChannel.get(channel)
  if (pubBridge) {
    sub.off("publication", pubBridge)
    publicationBridgeByChannel.delete(channel)
  }
  const errBridge = errorBridgeByChannel.get(channel)
  if (errBridge) {
    sub.off("error", errBridge)
    errorBridgeByChannel.delete(channel)
  }
  const historyHook = subscribedHistoryHookByChannel.get(channel)
  if (historyHook) {
    sub.off("subscribed", historyHook)
    subscribedHistoryHookByChannel.delete(channel)
  }
  subscriberSlotsByChannel.delete(channel)

  sub.unsubscribe()
  getClient().removeSubscription(sub)
  subscriptions.delete(channel)
  subRefCounts.delete(channel)
}

async function fetchCurrentRoundApi(
  roomId: string
): Promise<RoundSnapshot | null> {
  try {
    return await getCurrentRound(roomId)
  } catch {
    return null
  }
}

// ─── 10. Online-channel helpers ──────────────────────────────────────────────

function notifyOnlineCount(channel: string, count: number): void {
  const state = onlineChannelStateByChannel.get(channel)
  if (!state) return
  for (const slot of state.slots) {
    if (!slot.cancelled) slot.onCount(count)
  }
}

function notifyOnlineError(channel: string, err: Error): void {
  const state = onlineChannelStateByChannel.get(channel)
  if (!state) return
  for (const slot of state.slots) {
    if (!slot.cancelled) slot.onError?.(err)
  }
}

async function reconcileOnlineCount(channel: string): Promise<void> {
  const state = onlineChannelStateByChannel.get(channel)
  const sub = subscriptions.get(channel)
  if (!state || !sub || state.isReconciling) return

  state.isReconciling = true
  try {
    const stats = await sub.presenceStats()
    state.count = stats.numClients
    notifyOnlineCount(channel, state.count)
  } catch (e) {
    notifyOnlineError(channel, e instanceof Error ? e : new Error(String(e)))
  } finally {
    state.isReconciling = false
  }
}

// ─── 11. Per-user fan-out factory ────────────────────────────────────────────
//
// Both `subscribeBalance` and `subscribeGiftInventory` share the same shape:
//   1. lazily create a single Centrifuge subscription per channel;
//   2. fan-out incoming publications to N caller slots;
//   3. forward subscription errors;
//   4. invoke `onReconnect` on every reconnect (skipping the initial connect);
//   5. ref-count slots and tear down the underlying subscription when the
//      last slot unsubscribes.
//
// The factory below captures that shape generically. Each subscriber owns its
// own slot / connected-handler maps so different feeds never collide.

/**
 * Produces a `subscribe(accountId, options)` for a per-user Centrifugo
 * channel. Each subscriber maintains its own ref-counted slot registry, so
 * two feeds (e.g. balance + gift inventory) stay isolated.
 */
function createUserChannelSubscriber<TEvent>(
  config: UserChannelSubscriberConfig<TEvent>
): (
  accountId: string,
  options: UserChannelSubscribeOptions<TEvent>
) => () => void {
  const slotsByChannel = new Map<string, Set<UserChannelSlot<TEvent>>>()
  const connectedHandlerByChannel = new Map<string, () => void>()

  return (accountId, { onEvent, onReconnect }) => {
    const channel = config.buildChannel(accountId)
    const client = getClient()

    let sub = subscriptions.get(channel)
    const isNew = !sub

    if (!sub) {
      sub = client.newSubscription(channel)
      subscriptions.set(channel, sub)
      subRefCounts.set(channel, 0)

      const pubBridge = (ctx: { data: unknown }) => {
        const data = ctx.data as Record<string, unknown> | null | undefined
        if (!data) return
        const event = config.parsePublication(data)
        if (!event) return
        const slots = slotsByChannel.get(channel)
        if (!slots) return
        for (const slot of slots) {
          if (!slot.cancelled) slot.onEvent(event)
        }
      }
      publicationBridgeByChannel.set(channel, pubBridge)
      sub.on("publication", pubBridge)

      const errBridge = createErrorBridge(channel)
      errorBridgeByChannel.set(channel, errBridge)
      sub.on("error", errBridge)

      // `connected` fires on initial connect AND every reconnect. The first
      // firing is covered by the caller's REST prefetch (useBalance, gifts
      // prefetch) — skip it. Subsequent firings fan out to slots so they can
      // refetch and recover updates missed during the outage.
      let isFirstConnected = true
      const connectedHandler = () => {
        if (isFirstConnected) {
          isFirstConnected = false
          return
        }
        const slots = slotsByChannel.get(channel)
        if (!slots) return
        for (const slot of slots) {
          if (!slot.cancelled) slot.onReconnect?.()
        }
      }
      connectedHandlerByChannel.set(channel, connectedHandler)
      client.on("connected", connectedHandler)
    }

    subRefCounts.set(channel, (subRefCounts.get(channel) ?? 0) + 1)

    const slot: UserChannelSlot<TEvent> = {
      cancelled: false,
      onEvent,
      onReconnect,
    }
    let slots = slotsByChannel.get(channel)
    if (!slots) {
      slots = new Set()
      slotsByChannel.set(channel, slots)
    }
    slots.add(slot)

    if (isNew) sub.subscribe()

    return () => {
      slot.cancelled = true
      slotsByChannel.get(channel)?.delete(slot)

      const refs = (subRefCounts.get(channel) ?? 1) - 1
      subRefCounts.set(channel, refs)
      if (refs <= 0) {
        slotsByChannel.delete(channel)
        const connectedHandler = connectedHandlerByChannel.get(channel)
        if (connectedHandler) {
          client.off("connected", connectedHandler)
          connectedHandlerByChannel.delete(channel)
        }
        teardownSubscription(channel)
      }
    }
  }
}

// ─── 12. Public API ──────────────────────────────────────────────────────────

/**
 * Bootstrap room: API + Centrifugo subscription start simultaneously.
 * Snapshots from both go to `handlers.onSnapshot`; store version guard chooses
 * the freshest.
 *
 * @returns Unsubscribe function (cleans up subscription + abandons in-flight API).
 */
export function subscribeRoom(
  roomId: string,
  handlers: RoomHandlers,
  options: SubscribeRoomOptions = {}
): () => void {
  const { skipApiFetch = false } = options
  const channel = buildRoomChannel(roomId)
  let cancelled = false

  const slot: SubscriberSlot = {
    cancelled: false,
    onSnapshot: (snapshot, source, clockOffsetMs) => {
      if (!cancelled) handlers.onSnapshot(snapshot, source, clockOffsetMs)
    },
    onError: (err) => {
      if (!cancelled) handlers.onError(err)
    },
  }

  let slots = subscriberSlotsByChannel.get(channel)
  if (!slots) {
    slots = new Set()
    subscriberSlotsByChannel.set(channel, slots)
  }
  slots.add(slot)

  // Reuse existing subscription if already created (e.g. AppLoader + GamePage).
  const { sub, isNew } = ensureSubscription(channel)

  if (!skipApiFetch) {
    fetchCurrentRoundApi(roomId).then((snapshot) => {
      if (snapshot && !cancelled) handlers.onSnapshot(snapshot, "api")
    })
  }

  if (isNew) sub.subscribe()

  return () => {
    cancelled = true
    slot.cancelled = true
    subscriberSlotsByChannel.get(channel)?.delete(slot)

    const refs = (subRefCounts.get(channel) ?? 1) - 1
    subRefCounts.set(channel, refs)
    if (refs <= 0) {
      teardownSubscription(channel)
    }
  }
}

/**
 * Subscribe to online count on a specific channel.
 * Requires presence rights for subscribers in Centrifugo namespace config.
 */
export function subscribeOnlineCount(
  channel: string,
  handlers: OnlineCountHandlers
): () => void {
  const slot: OnlineSubscriberSlot = {
    cancelled: false,
    onCount: handlers.onCount,
    onError: handlers.onError,
  }

  const { sub, isNew } = ensureSubscription(channel)
  let state = onlineChannelStateByChannel.get(channel)

  if (!state) {
    state = {
      slots: new Set(),
      count: null,
      isReconciling: false,
      reconcileTimer: null,
      onSubscribed: () => {
        void reconcileOnlineCount(channel)
      },
      onJoin: () => {
        const current = onlineChannelStateByChannel.get(channel)
        if (!current || current.count == null) return
        current.count = Math.max(0, current.count + 1)
        notifyOnlineCount(channel, current.count)
      },
      onLeave: () => {
        const current = onlineChannelStateByChannel.get(channel)
        if (!current || current.count == null) return
        current.count = Math.max(0, current.count - 1)
        notifyOnlineCount(channel, current.count)
      },
      onError: (ctx) => {
        notifyOnlineError(
          channel,
          new Error(String(ctx.error?.message ?? ctx.error))
        )
      },
    }

    onlineChannelStateByChannel.set(channel, state)
    sub.on("subscribed", state.onSubscribed)
    sub.on("join", state.onJoin)
    sub.on("leave", state.onLeave)
    sub.on("error", state.onError)

    state.reconcileTimer = setInterval(() => {
      void reconcileOnlineCount(channel)
    }, ONLINE_RECONCILE_INTERVAL_MS)
  }

  state.slots.add(slot)

  if (state.count != null) {
    slot.onCount(state.count)
  }

  if (isNew) {
    sub.subscribe()
  } else {
    void reconcileOnlineCount(channel)
  }

  return () => {
    slot.cancelled = true

    const currentState = onlineChannelStateByChannel.get(channel)
    currentState?.slots.delete(slot)

    if (currentState && currentState.slots.size === 0) {
      if (currentState.reconcileTimer) {
        clearInterval(currentState.reconcileTimer)
        currentState.reconcileTimer = null
      }
      sub.off("subscribed", currentState.onSubscribed)
      sub.off("join", currentState.onJoin)
      sub.off("leave", currentState.onLeave)
      sub.off("error", currentState.onError)
      onlineChannelStateByChannel.delete(channel)
    }

    const refs = (subRefCounts.get(channel) ?? 1) - 1
    subRefCounts.set(channel, refs)
    if (refs <= 0) {
      teardownSubscription(channel)
    }
  }
}

/**
 * Subscribe to real-time balance updates for the current user.
 *
 * Channel: `realtime-personal:balance:user:{accountId}#{accountId}`.
 * The server publishes a {@link BalanceUpdatedEvent} after each confirmed
 * transaction. `new_balance` is the final value (not a delta) in atomic units.
 */
export const subscribeBalance = createUserChannelSubscriber<BalanceUpdatedEvent>(
  {
    buildChannel: buildBalanceChannel,
    parsePublication: (data) => {
      if (data["type"] !== "BalanceUpdated") return null
      return {
        type: "BalanceUpdated",
        asset_class: data["asset_class"] as "TON" | "TELEGRAM_GIFT",
        new_balance: data["new_balance"] as number,
      }
    },
  }
)

/**
 * Subscribe to the user's gift-inventory feed.
 *
 * Channel: `realtime-personal:gifts:user:{accountId}#{accountId}`.
 * The server publishes a {@link GiftInventoryUpdatedEvent} after every change
 * to the user's gift list (deposit / withdrawal / bet / hold release).
 * `items` is the full inventory snapshot — callers can simply replace their
 * local cache with `event.items`.
 */
export const subscribeGiftInventory =
  createUserChannelSubscriber<GiftInventoryUpdatedEvent>({
    buildChannel: buildGiftInventoryChannel,
    parsePublication: (data) => {
      if (data["type"] !== "GiftInventoryUpdated") return null
      const items = data["items"]
      if (!Array.isArray(items)) return null
      // Trust the backend shape — matches `GET /v1/user/gifts` per contract.
      return { type: "GiftInventoryUpdated", items: items as UserGift[] }
    },
  })

/**
 * Subscribe to the global tournaments feed.
 *
 * Channel: `realtime-events:games:tournaments` (static, shared by all clients).
 * The server publishes a {@link TournamentUpdatedEvent} after every tournament
 * update (e.g. a round resolving feeds the pool / leaderboard). The envelope is
 * the same `TournamentStateDTO` shape as bootstrap's `active_tournament`, but
 * `my_standing` is always null here.
 *
 * Built on the per-user fan-out factory with a static channel (the account id
 * is ignored); exposed as `subscribeTournaments(options)` — no account needed.
 */
const subscribeTournamentsRaw =
  createUserChannelSubscriber<TournamentUpdatedEvent>({
    buildChannel: () => buildTournamentsChannel(),
    parsePublication: (data) => {
      if (data["type"] !== "TournamentUpdated") return null
      const tournament = data["tournament"]
      if (!tournament || typeof tournament !== "object") return null
      return {
        type: "TournamentUpdated",
        server_time:
          typeof data["server_time"] === "number"
            ? (data["server_time"] as number)
            : undefined,
        // Defensive domain parsing happens downstream in `parseTournamentState`.
        tournament: tournament as TournamentUpdatedEvent["tournament"],
      }
    },
  })

export function subscribeTournaments(
  options: UserChannelSubscribeOptions<TournamentUpdatedEvent>
): () => void {
  return subscribeTournamentsRaw("", options)
}

/** Unsubscribe from a room. */
export function unsubscribeRoom(roomId: string): void {
  const channel = buildRoomChannel(roomId)
  teardownSubscription(channel)
}

/** Disconnect client (for app shutdown). */
export function disconnectCentrifugo(): void {
  for (const channel of subscriptions.keys()) {
    teardownSubscription(channel)
  }
  centrifugeInstance?.disconnect()
  centrifugeInstance = null
  setCentrifugeTokenSyncTarget(null)
  serverClockOffsetEstimator.reset()
}
